import { NETWORK_TYPE_OPTIONS, getNetworkTypeValue, toNetworkTypeAnswer } from '../../../utils/networkType';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Image,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { RcFile } from 'antd/es/upload';
import { FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { InformationCircleIcon } from 'hugeicons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

import { apiFetch } from 'services/apiClient';
import {
  fetchFieldInspectionControlTaskByReportFormat,
  previewFieldInspectionReportPdf,
} from '../../inspectors/services/fieldInspectionService';

dayjs.locale('tr');

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const MULTIPLE_TYPE = 'MULTIPLE';
const OTHER_CONTROL_AREA_VALUE = 'OTHER' as const;

type ControlAreaValue = number | typeof OTHER_CONTROL_AREA_VALUE;
type NoteMap = Record<number, string>;
type QuestionBundle = { question: any; answers: any[]; tableQuestions?: QuestionBundle[] };
type StaticValueFormEntry = {
  id?: number;
  name: string;
  functionTag?: string;
  value?: string;
  [key: string]: any;
};
type MultipleAnswerValue = {
  id: string | null;
  value?: string | null;
  abbreviation?: string | null;
  description?: string | null;
  isTrue?: boolean | null;
  type?: 'MULTIPLE';
};
type MultipleAnswerPayload = MultipleAnswerValue | MultipleAnswerValue[];
type ControlAreaOption = { id: ControlAreaValue; name: string; isOther?: boolean };

const questionTypeMap: Record<string, { label: string; color: string }> = {
  MULTIPLE: { label: 'Çoktan Seçmeli', color: 'blue' },
  FREETEXT: { label: 'Serbest Metin', color: 'purple' },
  NETWORK_TYPE: { label: 'Şebeke Tipi', color: 'cyan' },
  TITLE: { label: 'Ünvan', color: 'default' },
  ADDRESS: { label: 'Adres', color: 'default' },
  SGK_REGISTRATION_NUMBER: { label: 'SGK Sicil Numarası', color: 'default' },
  PHONE: { label: 'Telefon', color: 'default' },
  CONTRACT_ID: { label: 'ISG Katip Sözleşme ID', color: 'default' },
  EMAIL: { label: 'E-posta', color: 'default' },
  IDENTITY_NUMBER: { label: 'TC Kimlik No', color: 'cyan' },
  REGISTRATION_DATE: { label: 'Kalıcı Kayıt Tarihi', color: 'cyan' },
  WORK_EQUIPMENT: { label: 'İş Ekipmanı', color: 'cyan' },
  REPORT_NUMBER: { label: 'Rapor Numarası', color: 'default' },
  REPORT_DATE: { label: 'Rapor Tarihi', color: 'gold' },
  CONTROL_START_DATE: { label: 'Kontrol Başlangıç Tarihi', color: 'gold' },
  CONTROL_END_DATE: { label: 'Kontrol Bitiş Tarihi', color: 'gold' },
  NEXT_CONTROL_DATE: { label: 'Sonraki Kontrol Tarihi', color: 'gold' },
  STARTED_AT: { label: 'Başlangıç Saati', color: 'geekblue' },
  FINISHED_AT: { label: 'Bitiş Saati', color: 'geekblue' },
  NUMBER: { label: 'Sayı', color: 'green' },
  DATE: { label: 'Tarih', color: 'gold' },
  TIME: { label: 'Saat', color: 'geekblue' },
  YEAR: { label: 'Yıl', color: 'cyan' },
  IMAGE: { label: 'Görsel', color: 'magenta' },
  HVAC: { label: 'Havalandırma/Klima', color: 'orange' },
  PLUMBING: { label: 'Tesisat', color: 'volcano' },
  HYDRANT: { label: 'Hidrant', color: 'red' },
  CABINET: { label: 'Dolap', color: 'magenta' },
  FUNCTION: { label: 'Fonksiyon', color: 'lime' },
  TABLE: { label: 'Tablo Sorusu', color: 'blue' },
};

const ensureStringValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : String(value);
};

const toOptionalBoolean = (value: any): boolean | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return undefined;
};

const toOptionalNumber = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const toPositiveNumber = (value: unknown): number | undefined => {
  if (value == null) {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
};

const sortByOrder = (a: any, b: any) => {
  const first = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
  const second = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
  return first - second;
};

const normalizeQuestionDescriptionFields = (question: any) => {
  if (!question || typeof question !== 'object') {
    return question;
  }
  const normalizedDescription = ensureStringValue(
    question?.description ?? question?.questionDescription,
  ).trim();
  if (!normalizedDescription) {
    return question;
  }
  return {
    ...question,
    description: normalizedDescription,
    questionDescription: normalizedDescription,
  };
};

const normalizeStaticValueList = (input: any): StaticValueFormEntry[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input.map((item: any) => {
    const source = item && typeof item === 'object' ? { ...item } : {};
    const id = toOptionalNumber(source.id);
    const name = source.name != null ? String(source.name) : '';
    const rawTag = source.functionTag != null ? String(source.functionTag) : '';
    const functionTag = rawTag.trim().length ? rawTag.trim() : undefined;
    const value = ensureStringValue(source.value);
    return { ...source, id, name, functionTag, value };
  });
};

const buildStaticValueKey = (item: StaticValueFormEntry, index: number): string => {
  if (item.id !== undefined) return `id:${item.id}`;
  if (item.functionTag) return `tag:${item.functionTag}`;
  if (item.name) return `name:${item.name.toLowerCase()}`;
  return `index:${index}`;
};

const normalizeControlAreaId = (value: any): ControlAreaValue | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    if (trimmed.toUpperCase() === OTHER_CONTROL_AREA_VALUE) {
      return OTHER_CONTROL_AREA_VALUE;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value === 'object' && 'id' in value) {
    return normalizeControlAreaId(value.id);
  }
  return undefined;
};

const isNumericControlAreaId = (value: ControlAreaValue | undefined): value is number =>
  typeof value === 'number';

const getQuestionControlAreaIds = (question: any): number[] =>
  Array.isArray(question?.controlAreas)
    ? question.controlAreas
        .map((area: any) => normalizeControlAreaId(area))
        .filter((id: ControlAreaValue | undefined): id is number => typeof id === 'number')
    : [];

const compareControlAreaOptions = (first: ControlAreaOption, second: ControlAreaOption): number => {
  if (first.isOther === true && second.isOther !== true) {
    return 1;
  }
  if (first.isOther !== true && second.isOther === true) {
    return -1;
  }
  const nameCompare = first.name.localeCompare(second.name, 'tr', { numeric: true, sensitivity: 'base' });
  if (nameCompare !== 0) {
    return nameCompare;
  }
  return String(first.id).localeCompare(String(second.id), 'tr', { numeric: true, sensitivity: 'base' });
};

const isQuestionIncludedInInspectorForm = (question: any): boolean =>
  question?.excludeFromInspectorForm !== true;

const isVisibleInReportForm = (node: any): boolean => node?.showInReportForm !== false;

const filterNodesWithAncestors = (items: any[], predicate: (node: any) => boolean): any[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }
  const byId = new Map<number, any>();
  items.forEach((item) => {
    if (typeof item?.id === 'number') {
      byId.set(item.id, item);
    }
  });

  const includeIds = new Set<number>();
  const includeWithAncestors = (item: any) => {
    let current = item;
    while (current && typeof current?.id === 'number' && !includeIds.has(current.id)) {
      includeIds.add(current.id);
      const parentId = current?.parentId;
      current = typeof parentId === 'number' ? byId.get(parentId) : undefined;
    }
  };

  items.forEach((item) => {
    if (predicate(item)) {
      includeWithAncestors(item);
    }
  });

  return items.filter((item) => typeof item?.id === 'number' && includeIds.has(item.id));
};

const createMultipleAnswerFromOption = (option: any): MultipleAnswerValue | undefined => {
  if (!option || option.id === null || option.id === undefined) {
    return undefined;
  }
  return {
    id: String(option.id),
    value: option?.title ?? null,
    abbreviation: option?.abbreviation ?? null,
    description: null,
    isTrue: toOptionalBoolean(option?.isTrue) ?? null,
    type: MULTIPLE_TYPE,
  };
};

const normalizeMultipleAnswer = (value: any): MultipleAnswerValue | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const raw = value as Record<string, any>;
  const id = raw.id === null || raw.id === undefined || raw.id === '' ? null : String(raw.id);
  return {
    id,
    value: 'value' in raw ? (raw.value == null ? null : String(raw.value)) : undefined,
    abbreviation: 'abbreviation' in raw ? (raw.abbreviation == null ? null : String(raw.abbreviation)) : undefined,
    description: 'description' in raw ? (raw.description == null ? null : String(raw.description)) : undefined,
    isTrue: 'isTrue' in raw ? toOptionalBoolean(raw.isTrue) ?? null : undefined,
    type: typeof raw.type === 'string' ? (raw.type as any) : MULTIPLE_TYPE,
  };
};

const normalizeMultipleAnswers = (value: any): MultipleAnswerValue[] => {
  if (Array.isArray(value)) {
    const unique = new Map<string, MultipleAnswerValue>();
    value.forEach((item) => {
      const normalized = normalizeMultipleAnswer(item);
      if (normalized?.id) {
        unique.set(normalized.id, normalized);
      }
    });
    return Array.from(unique.values());
  }
  const single = normalizeMultipleAnswer(value);
  return single?.id ? [single] : [];
};

const isQuestionMultiSelectEnabled = (question: any): boolean =>
  question?.questionType === MULTIPLE_TYPE && question?.allowMultipleAnswers === true;

const buildMultipleAnswerPayload = (
  rawValue: any,
  answerOptions?: any[],
  allowMultipleAnswers = false,
): MultipleAnswerPayload | undefined => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return undefined;
  }
  if (allowMultipleAnswers) {
    const rawItems = Array.isArray(rawValue) ? rawValue : [rawValue];
    const deduped = new Map<string, MultipleAnswerValue>();
    rawItems.forEach((item) => {
      const id = typeof item === 'object' ? normalizeMultipleAnswer(item)?.id : String(item);
      if (!id) {
        return;
      }
      const option = answerOptions?.find((opt: any) => String(opt?.id) === id);
      deduped.set(id, createMultipleAnswerFromOption(option) ?? {
        id,
        value: null,
        abbreviation: null,
        description: null,
        isTrue: null,
        type: MULTIPLE_TYPE,
      });
    });
    return deduped.size ? Array.from(deduped.values()) : undefined;
  }
  const id = typeof rawValue === 'object' ? normalizeMultipleAnswer(rawValue)?.id : String(rawValue);
  if (!id) {
    return undefined;
  }
  const option = answerOptions?.find((opt: any) => String(opt?.id) === id);
  return createMultipleAnswerFromOption(option) ?? {
    id,
    value: null,
    abbreviation: null,
    description: null,
    isTrue: null,
    type: MULTIPLE_TYPE,
  };
};

const getDefaultMultipleAnswerPayload = (
  question: any,
  answerOptions: any[] | undefined,
): MultipleAnswerPayload | undefined => {
  if (!Array.isArray(answerOptions)) {
    return undefined;
  }
  const option = answerOptions.find((answer: any) => answer?.isDefault || answer?.default);
  const payload = createMultipleAnswerFromOption(option);
  if (!payload) {
    return undefined;
  }
  return isQuestionMultiSelectEnabled(question) ? [payload] : payload;
};

const isAnswerDescriptionRequired = (answer: any): boolean =>
  answer?.isDescriptionRequired === true || answer?.descriptionRequired === true;

const convertValueForStorage = (questionType: string, rawValue: any): any => {
  if (rawValue === null || rawValue === undefined) {
    return undefined;
  }
  switch (questionType) {
      case 'NETWORK_TYPE':
        return toNetworkTypeAnswer(rawValue);
    case 'NUMBER': {
      const numeric = Number(rawValue);
      return Number.isFinite(numeric) ? numeric : undefined;
    }
    case 'DATE':
    case 'REGISTRATION_DATE':
    case 'DIPLOMA_DATE':
    case 'REPORT_DATE':
    case 'CONTROL_START_DATE':
    case 'CONTROL_END_DATE':
      return dayjs.isDayjs(rawValue)
        ? rawValue.format('YYYY-MM-DD')
        : typeof rawValue === 'string'
        ? rawValue
        : undefined;
    case 'TIME':
    case 'STARTED_AT':
    case 'FINISHED_AT':
      return dayjs.isDayjs(rawValue)
        ? rawValue.format('HH:mm')
        : typeof rawValue === 'string'
        ? rawValue
        : undefined;
    case 'YEAR':
      return dayjs.isDayjs(rawValue)
        ? rawValue.format('YYYY')
        : typeof rawValue === 'string'
        ? rawValue
        : undefined;
    case 'IMAGE':
      return typeof rawValue === 'object' ? rawValue : undefined;
    default: {
      if (typeof rawValue === 'string') {
        return rawValue.trim().length ? rawValue : undefined;
      }
      return rawValue;
    }
  }
};

const toSafeDayjs = (value: unknown, format?: string) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (dayjs.isDayjs(value)) {
    return value;
  }
  const parsed = format ? dayjs(value as dayjs.ConfigType, format, true) : dayjs(value as dayjs.ConfigType);
  return parsed && typeof parsed.isValid === 'function' && parsed.isValid() ? parsed : null;
};

const parseTimeValue = (value: unknown) =>
  toSafeDayjs(value, 'HH:mm:ss') ?? toSafeDayjs(value, 'HH:mm');

const parseNumericLimit = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const isCurrentYearEnabled = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

const isCurrentDayEnabled = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

const getComparableNumericValue = (value: unknown, questionType?: string): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (questionType === 'YEAR') {
    if (dayjs.isDayjs(value)) {
      const year = value.year();
      return Number.isFinite(year) ? year : undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return undefined;
      }
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const getFieldValue = (questionType: string, storedValue: any): any => {
  if (storedValue === undefined) {
    return undefined;
  }
  switch (questionType) {
      case 'NETWORK_TYPE':
        return getNetworkTypeValue(storedValue);
    case 'MULTIPLE':
      return normalizeMultipleAnswer(storedValue)?.id ?? null;
    case 'NUMBER':
      return typeof storedValue === 'number' ? storedValue : Number(storedValue);
    case 'DATE':
    case 'REGISTRATION_DATE':
    case 'DIPLOMA_DATE':
    case 'REPORT_DATE':
    case 'CONTROL_START_DATE':
    case 'CONTROL_END_DATE':
      return toSafeDayjs(storedValue, 'YYYY-MM-DD');
    case 'TIME':
    case 'STARTED_AT':
    case 'FINISHED_AT':
      return parseTimeValue(storedValue);
    case 'YEAR':
      return toSafeDayjs(storedValue, 'YYYY');
    default:
      return storedValue;
  }
};

const buildQuestionRenderKey = (question: any, nodeId: number | string | undefined, index: number): string => {
  if (question?.id !== null && question?.id !== undefined) {
    return String(question.id);
  }
  return `${nodeId ?? 'node'}-${index}`;
};

const readFileAsDataUrl = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });

export default function ReportNodeViewerAll() {
  const { reportFormatUuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<any[]>([]);
  const [itemsByNode, setItemsByNode] = useState<Record<number, QuestionBundle[]>>({});
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [attributeStaticValues, setAttributeStaticValues] = useState<StaticValueFormEntry[]>([]);
  const [toolStaticValues, setToolStaticValues] = useState<StaticValueFormEntry[]>([]);
  const [selectedControlAreaId, setSelectedControlAreaId] = useState<ControlAreaValue | undefined>(undefined);
  const [noteContents, setNoteContents] = useState<NoteMap>({});
  const [controlAreas, setControlAreas] = useState<ControlAreaOption[]>([]);
  const [controlAreasLoading, setControlAreasLoading] = useState(false);
  const [controlAreasError, setControlAreasError] = useState<string | null>(null);
  const [attributeName, setAttributeName] = useState<string>('');
  const [attributeId, setAttributeId] = useState<number | undefined>(undefined);
  const [resolvedReportFormatId, setResolvedReportFormatId] = useState<number | undefined>(undefined);
  const [customControlAreaName, setCustomControlAreaName] = useState<string>('');
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      const reportFormatToken = String(reportFormatUuid ?? '').trim();
      if (!reportFormatToken) {
        setNodes([]);
        setItemsByNode({});
        setAttributeName('');
        setAttributeId(undefined);
        setResolvedReportFormatId(undefined);
        setError('Geçersiz rapor formatı bilgisi.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setAnswers({});
      setNoteContents({});
      setSelectedControlAreaId(undefined);
      setCustomControlAreaName('');
      try {
        const response = await fetchFieldInspectionControlTaskByReportFormat(reportFormatToken, true);
        if (ignore) {
          return;
        }

        setResolvedReportFormatId(toPositiveNumber(response?.reportFormat?.id));
        const resolvedAttributeId = toPositiveNumber(response?.reportFormat?.attribute?.id);
        setAttributeId(resolvedAttributeId);
        setAttributeName(response?.reportFormat?.attribute?.name || '');

        const normalizedNodes = (response?.nodes || []).map((node: any) => ({
          ...node,
          parentId:
            node?.parentId ??
            (typeof node?.parent === 'object' ? node.parent?.id : node?.parent),
        }));
        const directVisibleNodeIds = new Set(
          normalizedNodes
            .filter(isVisibleInReportForm)
            .map((node: any) => node?.id)
            .filter((id: unknown): id is number => typeof id === 'number'),
        );
        const visibleNodes = filterNodesWithAncestors(normalizedNodes, isVisibleInReportForm).sort(sortByOrder);

        const bundles: Record<number, QuestionBundle[]> = {};
        Object.entries(response?.itemsByNode || {}).forEach(([nodeIdRaw, items]) => {
          const nodeId = toPositiveNumber(nodeIdRaw);
          if (!nodeId || !directVisibleNodeIds.has(nodeId)) {
            return;
          }
          const normalizedItems = Array.isArray(items) ? items : [];
          bundles[nodeId] = normalizedItems
            .map((item: any) => ({
              question: normalizeQuestionDescriptionFields(item?.question),
              answers: Array.isArray(item?.answers) ? [...item.answers].sort(sortByOrder) : [],
              tableQuestions: Array.isArray(item?.tableQuestions) ? item.tableQuestions : [],
            }))
            .filter((item: QuestionBundle) => item.question && isQuestionIncludedInInspectorForm(item.question))
            .sort((a, b) => sortByOrder(a.question, b.question));
        });

        const defaultAnswers: Record<string, any> = {};
        Object.values(bundles).forEach((questionItems) => {
          questionItems.forEach((bundle) => {
            const question = bundle?.question;
            if (!question?.id) {
              return;
            }
            if (question.questionType === 'MULTIPLE') {
              const payload = getDefaultMultipleAnswerPayload(question, bundle.answers);
              if (payload) {
                defaultAnswers[String(question.id)] = Array.isArray(payload)
                  ? payload.map((item) => ({ ...item }))
                  : { ...payload };
              }
            }
            if (question.questionType === 'DATE' && isCurrentDayEnabled(question?.isCurrentDay)) {
              defaultAnswers[String(question.id)] = dayjs().format('YYYY-MM-DD');
            }
          });
        });

        setNodes(visibleNodes);
        setItemsByNode(bundles);
        setAnswers(defaultAnswers);
        setAttributeStaticValues(normalizeStaticValueList((response as any)?.attributeStaticValues));
        setToolStaticValues(normalizeStaticValueList((response as any)?.toolStaticValues));
      } catch (err) {
        if (!ignore) {
          console.error('ReportNodeViewerAll load failed', err);
          setNodes([]);
          setItemsByNode({});
          setAttributeName('');
          setAttributeId(undefined);
          setResolvedReportFormatId(undefined);
          setError('Rapor soruları yüklenirken bir hata oluştu.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [reportFormatUuid]);

  useEffect(() => {
    if (!attributeId) {
      setControlAreas([]);
      setControlAreasError(null);
      setControlAreasLoading(false);
      return;
    }

    let ignore = false;
    const loadControlAreas = async () => {
      setControlAreasLoading(true);
      setControlAreasError(null);
      try {
        const response = await apiFetch(`/control-areas?attributeId=${attributeId}`);
        if (ignore) {
          return;
        }
        const source = Array.isArray(response)
          ? response
          : (response as any)?.result ?? (response as any)?.data?.result ?? (response as any)?.data;
        const mapped: ControlAreaOption[] = Array.isArray(source)
          ? source.reduce<ControlAreaOption[]>((acc, item: any) => {
              const id = normalizeControlAreaId(item?.id);
              if (id === undefined) {
                return acc;
              }
              const rawName = ensureStringValue(item?.name).trim();
              acc.push({ id, name: rawName || `Tip - Özellik #${id}`, isOther: item?.isOther === true });
              return acc;
            }, []).sort(compareControlAreaOptions)
          : [];
        setControlAreas(mapped);
      } catch (err) {
        if (!ignore) {
          console.error('ReportNodeViewerAll: failed to load control areas', err);
          setControlAreas([]);
          setControlAreasError('Parametre tipleri - özellikleri yüklenemedi.');
        }
      } finally {
        if (!ignore) {
          setControlAreasLoading(false);
        }
      }
    };

    loadControlAreas();
    return () => {
      ignore = true;
    };
  }, [attributeId]);

  const childrenMap = useMemo(() => {
    const map: Record<number, any[]> = {};
    nodes.forEach((node: any) => {
      const parentId = node.parentId;
      if (parentId) {
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(node);
      }
    });
    Object.keys(map).forEach((key) => {
      map[Number(key)].sort(sortByOrder);
    });
    return map;
  }, [nodes]);

  const rootNodes = useMemo(() => nodes.filter((node: any) => !node.parentId), [nodes]);

  const selectedControlArea = useMemo(() => {
    if (selectedControlAreaId === undefined) {
      return undefined;
    }
    return controlAreas.find((area) => area.id === selectedControlAreaId);
  }, [controlAreas, selectedControlAreaId]);

  const isOtherControlAreaSelected =
    selectedControlAreaId === OTHER_CONTROL_AREA_VALUE || selectedControlArea?.isOther === true;
  const selectedControlAreaIdForSubmit = isOtherControlAreaSelected
    ? OTHER_CONTROL_AREA_VALUE
    : selectedControlAreaId;

  useEffect(() => {
    if (selectedControlAreaId !== OTHER_CONTROL_AREA_VALUE) {
      return;
    }
    const otherControlArea = controlAreas.find(
      (area) => area.isOther === true && isNumericControlAreaId(area.id),
    );
    if (otherControlArea) {
      setSelectedControlAreaId(otherControlArea.id);
    }
  }, [controlAreas, selectedControlAreaId]);

  const highlightedControlAreaQuestionIds = useMemo(() => {
    if (!isNumericControlAreaId(selectedControlAreaId) || isOtherControlAreaSelected) {
      return new Set<number>();
    }
    const matches = new Set<number>();
    Object.values(itemsByNode).forEach((bundles) => {
      bundles.forEach((bundle) => {
        const questionId = toPositiveNumber(bundle?.question?.id);
        if (!questionId) {
          return;
        }
        const hasMatch = getQuestionControlAreaIds(bundle.question).includes(selectedControlAreaId);
        if (hasMatch) {
          matches.add(questionId);
        }
      });
    });
    return matches;
  }, [isOtherControlAreaSelected, itemsByNode, selectedControlAreaId]);

  const handlePreviewPdf = useCallback(async () => {
    const reportFormatToken = String(reportFormatUuid ?? '').trim();
    if (!reportFormatToken) {
      message.error('Geçersiz rapor formatı bilgisi.');
      return;
    }
    const customControlAreaNameForSubmit =
      isOtherControlAreaSelected ? customControlAreaName.trim() : '';
    if (isOtherControlAreaSelected && !customControlAreaNameForSubmit) {
      message.error('Diğer Tip - Özellik bölümünün doldurulması zorunludur.');
      return;
    }

    setPreviewPdfLoading(true);
    let objectUrl: string | null = null;
    try {
      const { url } = await previewFieldInspectionReportPdf(reportFormatToken, {
        answers,
        notes: noteContents,
        selectedControlAreaId: selectedControlAreaIdForSubmit ?? null,
        customControlAreaName: customControlAreaNameForSubmit || null,
      });
      objectUrl = url;
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
      message.success('Rapor PDF ön izlemesi yeni sekmede açıldı.');
    } catch (err) {
      console.error('ReportNodeViewerAll: failed to preview report PDF', err);
      message.error('Rapor PDF ön izlemesi oluşturulurken bir hata oluştu.');
    } finally {
      if (objectUrl) {
        const urlToRevoke = objectUrl;
        objectUrl = null;
        window.setTimeout(() => URL.revokeObjectURL(urlToRevoke), 60000);
      }
      setPreviewPdfLoading(false);
    }
  }, [answers, customControlAreaName, isOtherControlAreaSelected, noteContents, reportFormatUuid, selectedControlAreaIdForSubmit]);

  const titleExtra = useMemo(() => (
    <Space>
      <Button
        type='primary'
        icon={<FilePdfOutlined />}
        onClick={handlePreviewPdf}
        loading={previewPdfLoading}
        disabled={loading || !!error}
      >
        PDF Ön İzleme
      </Button>
      <Button
        disabled={!resolvedReportFormatId}
        onClick={() => resolvedReportFormatId && navigate(`/report-formats/detail/${resolvedReportFormatId}?tab=1`)}
      >
        Geri
      </Button>
    </Space>
  ), [error, handlePreviewPdf, loading, navigate, previewPdfLoading, resolvedReportFormatId]);

  const handleAnswerChange = (
    question: any,
    rawValue: any,
    answerOptions?: any[],
  ) => {
    if (!question?.id || question?.questionType === 'FUNCTION') {
      return;
    }
    if (question?.questionType === 'NUMBER' || question?.questionType === 'YEAR') {
      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        const numeric = getComparableNumericValue(rawValue, question?.questionType);
        if (numeric === undefined) {
          message.error(question?.questionType === 'YEAR' ? 'Lütfen geçerli bir yıl seçiniz.' : 'Lütfen geçerli bir sayı giriniz.');
          return;
        }
        const min = parseNumericLimit(question?.minValue);
        const max = isCurrentYearEnabled(question?.isCurrentYear)
          ? new Date().getFullYear()
          : parseNumericLimit(question?.maxValue);
        if (min !== undefined && numeric < min) {
          message.error(`Bu alan için minimum değer ${min}.`);
          return;
        }
        if (max !== undefined && numeric > max) {
          message.error(`Bu alan için maksimum değer ${max}.`);
          return;
        }
      }
    }
    const key = String(question.id);
    const storedValue =
      question?.questionType === 'MULTIPLE'
        ? buildMultipleAnswerPayload(rawValue, answerOptions, isQuestionMultiSelectEnabled(question))
        : convertValueForStorage(question?.questionType, rawValue);
    setAnswers((prev) => {
      const next = { ...prev };
      if (storedValue === undefined) {
        delete next[key];
      } else {
        next[key] = Array.isArray(storedValue)
          ? storedValue.map((item) => ({ ...item }))
          : typeof storedValue === 'object' && storedValue !== null
          ? { ...storedValue }
          : storedValue;
      }
      return next;
    });
  };

  const handleImageBeforeUpload = async (question: any, file: RcFile) => {
    const dataUrl = await readFileAsDataUrl(file);
    handleAnswerChange(question, {
      name: file.name,
      type: file.type,
      size: file.size,
      dataUrl,
      uploadedAt: new Date().toISOString(),
    });
    return Upload.LIST_IGNORE;
  };

  const handleStaticValueChange = (
    setter: React.Dispatch<React.SetStateAction<StaticValueFormEntry[]>>,
    index: number,
    nextValue: string,
  ) => {
    setter((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const next = [...prev];
      next[index] = { ...next[index], value: nextValue };
      return next;
    });
  };

  const renderTableCellEditor = (
    question: any,
    column: any,
    cellValue: any,
    updateCell: (value: any) => void,
  ) => {
    const columnType = column?.columnType ?? column?.questionType;
    const options = Array.isArray(column?.options) ? column.options : Array.isArray(column?.answers) ? column.answers : [];
    const validateTableCellValue = (rawValue: any) => {
      if (columnType !== 'NUMBER' && columnType !== 'YEAR') {
        return true;
      }
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return true;
      }
      const numeric = getComparableNumericValue(rawValue, columnType);
      if (numeric === undefined) {
        message.error(columnType === 'YEAR' ? 'Lütfen geçerli bir yıl seçiniz.' : 'Lütfen geçerli bir sayı giriniz.');
        return false;
      }
      const min = parseNumericLimit(column?.minValue);
      const max = isCurrentYearEnabled(column?.isCurrentYear) ? new Date().getFullYear() : parseNumericLimit(column?.maxValue);
      if (min !== undefined && numeric < min) {
        message.error(`Bu alan için minimum değer ${min}.`);
        return false;
      }
      if (max !== undefined && numeric > max) {
        message.error(`Bu alan için maksimum değer ${max}.`);
        return false;
      }
      return true;
    };
    switch (columnType) {
      case 'NETWORK_TYPE':
        return (
          <Select
            aria-label='Şebeke Tipi'
            className='w-full'
            value={getNetworkTypeValue(cellValue)}
            options={NETWORK_TYPE_OPTIONS}
            allowClear
            placeholder='Seçiniz'
            onChange={(value) => updateCell(toNetworkTypeAnswer(value))}
          />
        );
      case 'MULTIPLE': {
        const normalized = normalizeMultipleAnswer(cellValue);
        return (
          <Select
            value={normalized?.id ?? null}
            allowClear
            placeholder='Seçenek'
            style={{ minWidth: 160, width: '100%' }}
            onChange={(value) => {
              const option = options.find((item: any) => String(item?.id) === String(value));
              updateCell(option ? createMultipleAnswerFromOption(option) : undefined);
            }}
          >
            {options.map((option: any) => (
              <Option key={option?.id} value={String(option?.id)}>{option?.title}</Option>
            ))}
          </Select>
        );
      }
      case 'NUMBER':
        return (
          <InputNumber
            value={typeof cellValue === 'number' ? cellValue : cellValue !== undefined && cellValue !== null && cellValue !== '' ? Number(cellValue) : undefined}
            style={{ width: '100%' }}
            onChange={(value) => {
              if (validateTableCellValue(value)) {
                updateCell(value);
              }
            }}
          />
        );
      case 'DATE':
      case 'REGISTRATION_DATE':
      case 'DIPLOMA_DATE':
      case 'REPORT_DATE':
      case 'CONTROL_START_DATE':
      case 'CONTROL_END_DATE':
        return (
          <DatePicker
            value={toSafeDayjs(cellValue, 'YYYY-MM-DD')}
            format='DD/MM/YYYY'
            style={{ width: '100%' }}
            onChange={(value) => updateCell(convertValueForStorage(columnType, value))}
          />
        );
      case 'TIME':
      case 'STARTED_AT':
      case 'FINISHED_AT':
        return (
          <TimePicker
            value={parseTimeValue(cellValue)}
            format='HH:mm'
            style={{ width: '100%' }}
            onChange={(value) => updateCell(convertValueForStorage(columnType, value))}
          />
        );
      case 'YEAR':
        return (
          <DatePicker
            picker='year'
            value={toSafeDayjs(cellValue, 'YYYY')}
            style={{ width: '100%' }}
            disabledDate={isCurrentYearEnabled(column?.isCurrentYear) ? (current: any) => current?.year?.() > dayjs().year() : undefined}
            onChange={(value) => {
              if (validateTableCellValue(value)) {
                updateCell(convertValueForStorage(columnType, value));
              }
            }}
          />
        );
      case 'FUNCTION':
        return <Input value={cellValue !== undefined && cellValue !== null ? String(cellValue) : ''} readOnly placeholder='Bu hücre otomatik hesaplanır' />;
      default:
        return (
          <Input
            value={typeof cellValue === 'string' || typeof cellValue === 'number' ? String(cellValue) : ''}
            placeholder='Hücre'
            onChange={(event) => updateCell(event.target.value)}
          />
        );
    }
  };

  const renderTableAnswerField = (question: any, tableQuestions: QuestionBundle[]) => {
    const key = question?.id != null ? String(question.id) : undefined;
    if (!key) {
      return <Alert type='warning' message='Soru kimliği bulunamadı.' showIcon />;
    }
    const columns = Array.isArray(tableQuestions)
      ? tableQuestions
          .map((bundle) => ({ ...(bundle?.question || {}), answers: Array.isArray(bundle?.answers) ? bundle.answers : [] }))
          .sort(sortByOrder)
      : [];
    if (!columns.length) {
      return <Alert type='info' message='Bu tablo sorusu için tablo kolonu tanımlı değil.' showIcon />;
    }
    const storedValue = answers[key];
    const rows = Array.isArray(storedValue?.rows) ? storedValue.rows : [];
    const setRows = (nextRows: any[]) => {
      setAnswers((prev) => ({ ...prev, [key]: { rows: nextRows } }));
    };
    return (
      <Space direction='vertical' size={12} style={{ width: '100%' }}>
        <InputNumber
          min={0}
          precision={0}
          value={rows.length}
          addonBefore='Satır Sayısı'
          onChange={(countValue) => {
            const count = Number.isFinite(Number(countValue)) && Number(countValue) > 0 ? Number(countValue) : 0;
            const nextRows = Array.from({ length: count }, (_, index) => {
              const existing = rows[index];
              return existing && typeof existing === 'object'
                ? { ...existing, rowOrder: index + 1, cells: existing.cells || {} }
                : { rowOrder: index + 1, cells: {} };
            });
            setRows(nextRows);
          }}
          style={{ width: 220 }}
        />
        {rows.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: columns.length * 180 }}>
              <thead>
                <tr>
                  <th style={{ width: 72, border: '1px solid #f0f0f0', padding: 8, textAlign: 'left' }}>Satır</th>
                  {columns.map((column) => (
                    <th key={column.id} style={{ border: '1px solid #f0f0f0', padding: 8, textAlign: 'left' }}>
                      {column.title || 'Tablo Kolonu'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row: any, rowIndex: number) => (
                  <tr key={rowIndex}>
                    <td style={{ border: '1px solid #f0f0f0', padding: 8 }}>{rowIndex + 1}</td>
                    {columns.map((column) => {
                      const cellValue = row?.cells?.[String(column.id)];
                      return (
                        <td key={column.id} style={{ border: '1px solid #f0f0f0', padding: 8, minWidth: 160 }}>
                          {renderTableCellEditor(question, column, cellValue, (value) => {
                            const nextRows = rows.map((currentRow: any, index: number) => ({
                              rowOrder: index + 1,
                              cells: { ...(currentRow?.cells || {}) },
                            }));
                            const columnKey = String(column.id);
                            if (value === undefined || value === null || value === '') {
                              delete nextRows[rowIndex].cells[columnKey];
                            } else {
                              nextRows[rowIndex].cells[columnKey] = value;
                            }
                            setRows(nextRows);
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Space>
    );
  };

  const renderAnswerField = (question: any, answerOptions: any[], tableQuestions?: QuestionBundle[]) => {
    const key = question?.id != null ? String(question.id) : undefined;
    if (!key) {
      return <Alert type='warning' message='Soru kimliği bulunamadı.' showIcon />;
    }
    const storedValue = answers[key];
    const fieldValue = getFieldValue(question?.questionType, storedValue);
    const isMultiSelectMultiple = isQuestionMultiSelectEnabled(question);
    const storedMultipleValue = question?.questionType === 'MULTIPLE' && !isMultiSelectMultiple
      ? normalizeMultipleAnswer(storedValue)
      : undefined;
    const storedMultipleValues = question?.questionType === 'MULTIPLE' && isMultiSelectMultiple
      ? normalizeMultipleAnswers(storedValue)
      : [];
    const defaultMultipleValue = question?.questionType === 'MULTIPLE'
      ? getDefaultMultipleAnswerPayload(question, answerOptions)
      : undefined;
    const defaultMultipleSingle = Array.isArray(defaultMultipleValue) ? defaultMultipleValue[0] : defaultMultipleValue;
    const defaultMultipleValues = Array.isArray(defaultMultipleValue)
      ? defaultMultipleValue
      : defaultMultipleValue
      ? [defaultMultipleValue]
      : [];
    const effectiveMultipleValue = question?.questionType === 'MULTIPLE'
      ? isMultiSelectMultiple
        ? undefined
        : storedMultipleValue?.id ?? defaultMultipleSingle?.id ?? null
      : fieldValue;
    const effectiveMultipleValues = question?.questionType === 'MULTIPLE' && isMultiSelectMultiple
      ? (storedMultipleValues.length ? storedMultipleValues : defaultMultipleValues)
          .map((answer) => answer?.id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      : [];
    const selectedMultipleOption =
      question?.questionType === 'MULTIPLE' &&
      !isMultiSelectMultiple &&
      Array.isArray(answerOptions) &&
      effectiveMultipleValue
        ? answerOptions.find((answer: any) => String(answer?.id) === String(effectiveMultipleValue))
        : undefined;
    const requiresMultipleDescription = isAnswerDescriptionRequired(selectedMultipleOption);
    const multipleDescriptionValue = ensureStringValue(storedMultipleValue?.description);

    switch (question?.questionType) {
      case 'NETWORK_TYPE':
        return (
          <Select
            aria-label='Şebeke Tipi'
            className='w-full'
            value={fieldValue}
            options={NETWORK_TYPE_OPTIONS}
            allowClear
            placeholder='Seçiniz'
            onChange={(value) => handleAnswerChange(question, value)}
          />
        );
      case 'TABLE':
        return renderTableAnswerField(question, tableQuestions || []);
      case 'MULTIPLE':
        if (!answerOptions?.length) {
          return <Alert type='info' message='Bu soru için seçenek tanımlı değil.' showIcon />;
        }
        if (isMultiSelectMultiple) {
          return (
            <Checkbox.Group
              value={effectiveMultipleValues}
              onChange={(values) => handleAnswerChange(question, values, answerOptions)}
              style={{ width: '100%' }}
            >
              <Space direction='vertical'>
                {answerOptions.map((answer: any) => (
                  <Checkbox key={answer?.id ?? answer?.title} value={String(answer?.id)}>
                    {answer?.title}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          );
        }
        return (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Radio.Group
              value={effectiveMultipleValue ?? null}
              onChange={(event) => handleAnswerChange(question, event.target.value, answerOptions)}
            >
              <Space direction='vertical'>
                {answerOptions.map((answer: any) => (
                  <Radio key={answer?.id ?? answer?.title} value={String(answer?.id)}>
                    {answer?.title}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            {requiresMultipleDescription ? (
              <TextArea
                value={multipleDescriptionValue}
                rows={3}
                placeholder='Açıklama uygunsuzluk nedeni seçiminden doldurulur'
                readOnly
              />
            ) : null}
          </Space>
        );
      case 'NUMBER':
        return (
          <InputNumber
            value={typeof fieldValue === 'number' ? fieldValue : undefined}
            style={{ width: '100%' }}
            onChange={(value) => handleAnswerChange(question, value)}
          />
        );
      case 'DATE':
      case 'REGISTRATION_DATE':
      case 'DIPLOMA_DATE':
      case 'REPORT_DATE':
      case 'CONTROL_START_DATE':
      case 'CONTROL_END_DATE':
        return (
          <DatePicker
            value={toSafeDayjs(fieldValue, 'YYYY-MM-DD')}
            format='DD/MM/YYYY'
            style={{ width: '100%' }}
            onChange={(value) => handleAnswerChange(question, value)}
          />
        );
      case 'TIME':
      case 'STARTED_AT':
      case 'FINISHED_AT':
        return (
          <TimePicker
            value={toSafeDayjs(fieldValue, 'HH:mm')}
            format='HH:mm'
            style={{ width: '100%' }}
            onChange={(value) => handleAnswerChange(question, value)}
          />
        );
      case 'YEAR':
        return (
          <DatePicker
            picker='year'
            value={toSafeDayjs(fieldValue, 'YYYY')}
            style={{ width: '100%' }}
            disabledDate={isCurrentYearEnabled(question?.isCurrentYear) ? (current: any) => current?.year?.() > dayjs().year() : undefined}
            onChange={(value) => handleAnswerChange(question, value)}
          />
        );
      case 'IMAGE': {
        const imageValue = fieldValue;
        return (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Upload
              accept='image/*'
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => handleImageBeforeUpload(question, file)}
            >
              <Button icon={<UploadOutlined />}>Görsel Yükle</Button>
            </Upload>
            {imageValue?.dataUrl ? (
              <Space direction='vertical' size={6}>
                <Image
                  src={imageValue.dataUrl}
                  alt={imageValue.name ?? question?.title}
                  width={240}
                  style={{ borderRadius: 8 }}
                />
                <Space size={8}>
                  <Text type='secondary'>{imageValue?.name}</Text>
                  <Button danger size='small' onClick={() => handleAnswerChange(question, null)}>
                    Görseli Kaldır
                  </Button>
                </Space>
              </Space>
            ) : null}
          </Space>
        );
      }
      case 'FUNCTION':
        return (
          <TextArea
            value={fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : ''}
            rows={3}
            readOnly
            placeholder='Bu alan otomatik hesaplanır'
          />
        );
      case 'TITLE':
        return <Input value={typeof fieldValue === 'string' ? fieldValue : ''} readOnly placeholder='Firma ünvanı otomatik gelir' />;
      case 'ADDRESS':
        return <TextArea value={typeof fieldValue === 'string' ? fieldValue : ''} rows={3} readOnly placeholder='Adres otomatik gelir' />;
      case 'SGK_REGISTRATION_NUMBER':
        return <Input type='number' value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='SGK numarası otomatik gelir' />;
      case 'PHONE':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Firma telefonu otomatik gelir' />;
      case 'EMAIL':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Firma e-postası otomatik gelir' />;
      case 'CONTRACT_ID':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Sözleşme bilgisi otomatik gelir' />;
      case 'FIRST_NAME':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Ad bilgisi otomatik gelir' />;
      case 'LAST_NAME':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Soyad bilgisi otomatik gelir' />;
      case 'PROFESSION':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Meslek bilgisi otomatik gelir' />;
      case 'REGISTRATION_NUMBER':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Kayıt numarası otomatik gelir' />;
      case 'DIPLOMA_NUMBER':
        return <Input value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''} readOnly placeholder='Diploma numarası otomatik gelir' />;
      case 'IDENTITY_NUMBER':
        return (
          <Input
            type='number'
            value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''}
            onChange={(event) => handleAnswerChange(question, event.target.value)}
          />
        );
      case 'FREETEXT':
      case 'HVAC':
      case 'PLUMBING':
      case 'HYDRANT':
      case 'CABINET':
      case 'WORK_EQUIPMENT':
      default:
        return (
          <TextArea
            value={typeof fieldValue === 'string' ? fieldValue : ''}
            rows={question?.questionType === 'FREETEXT' ? 4 : 3}
            placeholder='Yanıtınızı giriniz'
            onChange={(event) => handleAnswerChange(question, event.target.value)}
          />
        );
    }
  };

  const renderNode = (node: any, depth: number): React.ReactNode => {
    const headingLevel = Math.min(3 + depth, 5);
    const bundles = itemsByNode[node.id] || [];
    const children = childrenMap[node.id] || [];
    const isNoteNode = node?.type === 'NOTE';
    const noteValue = isNoteNode && node?.id != null ? noteContents[Number(node.id)] ?? '' : '';

    return (
      <div key={node.id} style={{ marginBottom: depth === 0 ? 32 : 24 }}>
        <Title level={headingLevel as 3 | 4 | 5} style={{ marginBottom: 0 }}>
          {node.title}
        </Title>
        {children.length === 0 ? (
          <>
            <Divider style={{ margin: depth === 0 ? '12px 0 16px' : '8px 0 12px' }} />
            {isNoteNode ? (
              <div style={{ marginBottom: 16 }}>
                <Space direction='vertical' size={6} style={{ width: '100%' }}>
                  <Text strong>Not</Text>
                  <TextArea
                    rows={4}
                    placeholder='Not giriniz'
                    value={noteValue}
                    onChange={(event) =>
                      setNoteContents((prev) => ({
                        ...prev,
                        [Number(node.id)]: typeof event.target.value === 'string' ? event.target.value : '',
                      }))
                    }
                  />
                </Space>
              </div>
            ) : null}
            <Row gutter={[16, 16]} wrap>
              {(() => {
                let questionDisplayIndex = 0;
                return bundles.map((bundle, index) => {
                  const question = bundle.question;
                  const questionDescription = ensureStringValue(
                    question?.description ?? question?.questionDescription,
                  ).trim();
                  const typeInfo = question?.questionType ? questionTypeMap[question.questionType] : undefined;
                  const questionIdNumeric = toPositiveNumber(question?.id);
                  const questionControlAreaIds = getQuestionControlAreaIds(question);
                  const isFilteringByControlArea =
                    isNumericControlAreaId(selectedControlAreaId) && !isOtherControlAreaSelected;
                  const hasAssignedControlArea = questionControlAreaIds.length > 0;
                  const isControlAreaHighlighted =
                    isFilteringByControlArea &&
                    questionIdNumeric !== undefined &&
                    highlightedControlAreaQuestionIds.has(questionIdNumeric);
                  const shouldHideQuestion =
                    isFilteringByControlArea &&
                    hasAssignedControlArea &&
                    !isControlAreaHighlighted;
                  if (shouldHideQuestion) {
                    return null;
                  }
                  questionDisplayIndex += 1;
                  const isWorkEquipmentRequired =
                    isOtherControlAreaSelected &&
                    question?.questionType === 'WORK_EQUIPMENT';
                  const questionContent = (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <Space size={8} wrap>
                          <Text strong>{questionDisplayIndex}.</Text>
                          <Text strong>{question?.title}</Text>
                          {questionDescription ? (
                            <Tooltip title={questionDescription}>
                              <InformationCircleIcon
                                size={16}
                                style={{ color: '#1677ff', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          ) : null}
                          {typeInfo ? <Tag color={typeInfo.color}>{typeInfo.label}</Tag> : null}
                          {question?.isRequired || isWorkEquipmentRequired ? <Tag color='red'>Zorunlu</Tag> : null}
                          {question?.isCritical ? <Tag color='volcano'>Kritik</Tag> : null}
                          {isControlAreaHighlighted ? (
                            <Tag color='gold'>
                              {selectedControlArea?.name
                                ? `${selectedControlArea.name} ile eşleşti`
                                : 'Tip - Özellik eşleşti'}
                            </Tag>
                          ) : null}
                        </Space>
                      </div>
                      <div style={{ paddingLeft: 24, paddingRight: 8 }}>
                        {renderAnswerField(question, bundle.answers, bundle.tableQuestions)}
                      </div>
                    </>
                  );
                  return (
                    <Col key={buildQuestionRenderKey(question, node?.id, index)} span={24}>
                      {isControlAreaHighlighted ? (
                        <div
                          style={{
                            borderRadius: 12,
                            border: '1px solid #faad14',
                            padding: 12,
                            backgroundColor: 'rgba(250, 173, 20, 0.08)',
                          }}
                        >
                          {questionContent}
                        </div>
                      ) : (
                        questionContent
                      )}
                    </Col>
                  );
                });
              })()}
            </Row>
          </>
        ) : null}
        {children.map((child: any) => renderNode(child, depth + 1))}
      </div>
    );
  };

  const renderStaticValuesSection = (
    title: string,
    values: StaticValueFormEntry[],
    setter: React.Dispatch<React.SetStateAction<StaticValueFormEntry[]>>,
  ) => {
    if (!values.length) {
      return null;
    }
    return (
      <Space direction='vertical' size={12} style={{ width: '100%' }}>
        {values.map((item, index) => {
          const key = buildStaticValueKey(item, index);
          const label = item.name && item.name.trim().length ? item.name : `Sabit Değer #${index + 1}`;
          return (
            <div key={key} style={{ width: '100%' }}>
              <Space direction='vertical' size={4} style={{ width: '100%' }}>
                <Space size={8} wrap>
                  <Text strong>{label}</Text>
                  {item.functionTag ? <Tag color='geekblue'>{item.functionTag}</Tag> : null}
                </Space>
                <Input
                  value={item.value ?? ''}
                  onChange={(event) => handleStaticValueChange(setter, index, event.target.value)}
                  placeholder={`${title} değeri giriniz`}
                  allowClear
                />
              </Space>
            </div>
          );
        })}
      </Space>
    );
  };

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <Card className='br-30px' title='Kontrol Bilgileri' extra={titleExtra}>
        <Descriptions
          bordered
          size='small'
          column={1}
          labelStyle={{ width: 220, maxWidth: 220, fontWeight: 600, color: 'rgba(0,0,0,0.88)' }}
        >
          <Descriptions.Item label='Kontrol ID'>-</Descriptions.Item>
          <Descriptions.Item label='Kontrol Sırası'>-</Descriptions.Item>
          <Descriptions.Item label='Parametre'>{attributeName || '-'}</Descriptions.Item>
          <Descriptions.Item label='Kontrol Süresi'>00:00</Descriptions.Item>
        </Descriptions>
      </Card>

      {(controlAreasLoading || controlAreasError || controlAreas.length > 0) && (
        <Card className='br-30px' title='Tip - Özellik'>
          {controlAreasLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : controlAreasError ? (
            <Alert type='warning' message={controlAreasError} showIcon />
          ) : (
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Select
                showSearch
                allowClear
                placeholder='Tip - Özellik seçin'
                style={{ width: '100%' }}
                value={selectedControlAreaId ?? undefined}
                optionFilterProp='label'
                onChange={(value) => {
                  const normalized = value === undefined ? undefined : normalizeControlAreaId(value);
                  setSelectedControlAreaId(normalized);
                  const selectedOption = controlAreas.find((area) => area.id === normalized);
                  const nextIsOther = normalized === OTHER_CONTROL_AREA_VALUE || selectedOption?.isOther === true;
                  if (!nextIsOther) {
                    setCustomControlAreaName('');
                  }
                }}
                options={controlAreas.map((area) => ({ label: area.name, value: area.id }))}
              />
              {isOtherControlAreaSelected ? (
                <Input
                  value={customControlAreaName}
                  onChange={(event) => setCustomControlAreaName(event.target.value)}
                  placeholder='MAKİNA ADI - EKİPMAN ÖZELLİĞİ gibi'
                  maxLength={255}
                  allowClear
                />
              ) : null}
            </Space>
          )}
        </Card>
      )}

      {(toolStaticValues.length > 0 || attributeStaticValues.length > 0) && (
        <Row gutter={[16, 16]}>
          {toolStaticValues.length > 0 ? (
            <Col xs={24} md={12}>
              <Card className='br-30px' title='Bileşen Sabit Değerleri'>
                {renderStaticValuesSection('Bileşen', toolStaticValues, setToolStaticValues)}
              </Card>
            </Col>
          ) : null}
          {attributeStaticValues.length > 0 ? (
            <Col xs={24} md={12}>
              <Card className='br-30px' title='Parametre Sabit Değerleri'>
                {renderStaticValuesSection('Parametre', attributeStaticValues, setAttributeStaticValues)}
              </Card>
            </Col>
          ) : null}
        </Row>
      )}

      <Card className='br-30px' title='Rapor Soruları'>
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : error ? (
          <Alert type='warning' message={error} showIcon />
        ) : rootNodes.length ? (
          rootNodes.map((node: any) => renderNode(node, 0))
        ) : (
          <Alert type='info' message='Bu rapor formatı için tanımlı soru bulunamadı.' showIcon />
        )}
      </Card>
    </Space>
  );
}
